import { useState } from "react";
import { motion } from "framer-motion";
import { Search, Package, CheckCircle2, Wrench, Inbox, XCircle, Truck } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const STEP_ICON = {
  neu: Inbox,
  in_bearbeitung: Wrench,
  unterwegs: Truck,
  abgeschlossen: CheckCircle2,
  storniert: XCircle,
};

export default function Tracking() {
  const [ref, setRef] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const search = async (e) => {
    e.preventDefault();
    if (!ref.trim()) return;
    setLoading(true); setError(""); setResult(null);
    try {
      const { data } = await api.get(`/orders/track/${ref.trim().toUpperCase()}`);
      setResult(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Nicht gefunden");
    } finally { setLoading(false); }
  };

  const Icon = result ? (STEP_ICON[result.status] || Package) : Package;

  return (
    <section id="verfolgen" data-testid="tracking" className="relative z-10 bg-[#0a0a0a] py-24 lg:py-36 border-t border-white/10">
      <div className="max-w-3xl mx-auto px-6 lg:px-10">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#888] mb-4">Sendungsverfolgung</p>
        <h1 className="font-display text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.95] mb-8">
          Wo ist mein iPhone?
        </h1>

        <form onSubmit={search} data-testid="track-form" className="flex flex-col sm:flex-row gap-3">
          <input
            data-testid="track-input"
            value={ref}
            onChange={(e) => setRef(e.target.value)}
            placeholder="Referenznummer, z. B. GR-3BFD64"
            className="flex-1 bg-[#050505] border border-white/10 rounded-full px-6 py-4 text-white placeholder:text-[#666] focus:border-white/40 focus:outline-none transition-colors duration-300"
          />
          <button type="submit" disabled={loading} data-testid="track-submit"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-[#050505] px-8 py-4 font-medium hover:bg-[#d1d5db] transition-colors disabled:opacity-50">
            <Search size={18} /> {loading ? "Suche…" : "Verfolgen"}
          </button>
        </form>

        {error && <p data-testid="track-error" className="text-red-400 text-sm mt-4">{error}</p>}

        {result && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="track-result"
            className="mt-8 rounded-3xl border border-white/10 bg-[#050505] p-8"
          >
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-white text-[#050505] flex items-center justify-center">
                <Icon size={24} />
              </div>
              <div>
                <p className="font-mono text-xs uppercase tracking-widest text-[#888]">{result.reference}</p>
                <p className="font-display text-3xl text-white" data-testid="track-status">{result.status_label}</p>
              </div>
            </div>
            <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
              <div><p className="text-[#888]">Gerät</p><p className="text-white mt-1">{result.device}</p></div>
              <div><p className="text-[#888]">Reparatur</p><p className="text-white mt-1">{result.service_label}</p></div>
              <div><p className="text-[#888]">Bestellt am</p><p className="text-white mt-1">{new Date(result.created_at).toLocaleDateString("de-DE")}</p></div>
              <div><p className="text-[#888]">Gesamt</p><p className="text-white mt-1 font-mono">{result.total.toFixed(2)} €</p></div>
            </div>
            {result.tracking_number && (
              <div data-testid="track-shipping" className="mt-6 rounded-2xl border border-white/10 bg-[#0a0a0a] p-5 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-yellow-400 text-[#050505] flex items-center justify-center shrink-0">
                  <Truck size={20} />
                </div>
                <div>
                  <p className="text-white font-medium">Unterwegs mit DHL</p>
                  <p data-testid="track-shipping-number" className="font-mono text-sm text-[#aaa] mt-1">{result.tracking_number}</p>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </div>
    </section>
  );
}
